import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";
import { createApiClient } from "@/api/client";
import type { RepoDraft } from "@/types";

// #278: Schreib-Seite des Repo-Lifecycle. Jede Aktion invalidiert BEIDE Seiten — die
// Entwurfs-Liste (Zustand ändert sich) und die Inhalts-Queries des Repos (ein Merge ändert
// den Live-Stand). Ohne das zeigte der Cache nach einer Freigabe noch den alten Inhalt.

function useInvalidateLifecycle() {
  const qc = useQueryClient();
  return (repoId: string) => {
    for (const part of ["drafts", "changes", "versions", "tree", "file"]) {
      void qc.invalidateQueries({ queryKey: ["resources", "git-content", part, repoId] });
    }
    void qc.invalidateQueries({ queryKey: ["resources", "git-content", "repos"] });
    // #291: Lifecycle-Aktionen ändern die Freigabe-Arbeitslisten UND (nach einem Merge)
    // den Live-Katalog — die Fach-Sichten müssen sofort nachziehen.
    void qc.invalidateQueries({ queryKey: ["resources", "git-content", "pending-drafts"] });
    void qc.invalidateQueries({ queryKey: ["resources", "marketplaces"] });
    void qc.invalidateQueries({ queryKey: ["resources", "plugins"] });
  };
}

export function useCreateDraft(): UseMutationResult<RepoDraft, Error, { repoId: string; slug: string }> {
  const { session, logout } = useAuth();
  const invalidate = useInvalidateLifecycle();
  return useMutation({
    mutationFn: ({ repoId, slug }) => {
      const client = createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
      return client.post<RepoDraft>(`/git-content/repos/${repoId}/drafts`, { slug });
    },
    onSuccess: (_d, v) => invalidate(v.repoId),
  });
}

export function useDiscardDraft(): UseMutationResult<unknown, Error, { repoId: string; branch: string }> {
  const { session, logout } = useAuth();
  const invalidate = useInvalidateLifecycle();
  return useMutation({
    mutationFn: ({ repoId, branch }) => {
      const client = createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
      return client.delete<unknown>(`/git-content/repos/${repoId}/drafts/${encodeURIComponent(branch)}`);
    },
    onSuccess: (_d, v) => invalidate(v.repoId),
  });
}

export function useSubmitDraft(): UseMutationResult<
  RepoDraft,
  Error,
  { repoId: string; branch: string; title: string; description?: string }
> {
  const { session, logout } = useAuth();
  const invalidate = useInvalidateLifecycle();
  return useMutation({
    mutationFn: ({ repoId, branch, title, description }) => {
      const client = createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
      return client.post<RepoDraft>(
        `/git-content/repos/${repoId}/drafts/${encodeURIComponent(branch)}/submit`,
        { title, description },
      );
    },
    onSuccess: (_d, v) => invalidate(v.repoId),
  });
}

export interface ApproveResult {
  merged: boolean;
  mergedSha: string | null;
  tagged: boolean;
  tagError: string | null;
  overridden: boolean;
}

export function useApproveDraft(): UseMutationResult<
  ApproveResult,
  Error,
  { repoId: string; branch: string; tag?: string; message?: string; override?: boolean; overrideReason?: string }
> {
  const { session, logout } = useAuth();
  const invalidate = useInvalidateLifecycle();
  return useMutation({
    mutationFn: ({ repoId, branch, tag, message, override, overrideReason }) => {
      const client = createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
      return client.post<ApproveResult>(
        `/git-content/repos/${repoId}/drafts/${encodeURIComponent(branch)}/approve`,
        {
          ...(tag ? { tag } : {}),
          ...(message ? { message } : {}),
          ...(override ? { override: true, overrideReason } : {}),
        },
      );
    },
    onSuccess: (_d, v) => invalidate(v.repoId),
  });
}
// `useCreateVersion` wurde entfernt (Review-Fund #10: kein einziger Aufrufer — vorbereiteter
// toter Code). Der Server-Endpunkt `POST /repos/:id/versions` existiert weiter; wenn die
// Versions-Erstellung in die Oberfläche kommt, liegt der Hook in der Git-Historie.
